import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Button } from '@/shared/components/ui/Button';
import { Calendar, Download, FileSpreadsheet, FileText } from 'lucide-react';

interface LedgerMasterItem {
  _id: string;
  code: string;
  name: string;
  type: string;
  normalBalance: string;
  primaryGroup: string;
  subGroup: string;
  openingBalance: number;
  debit: number;
  credit: number;
  currentBalance: number;
  entriesCount: number;
  isActive: boolean;
}

const FINANCIAL_YEARS = [
  { label: 'FY 2026-2027', startDate: '2026-04-01', endDate: '2027-03-31', display: 'Apr 01, 2026 - Mar 31, 2027' },
  { label: 'FY 2025-2026', startDate: '2025-04-01', endDate: '2026-03-31', display: 'Apr 01, 2025 - Mar 31, 2026' },
  { label: 'FY 2024-2025', startDate: '2024-04-01', endDate: '2025-03-31', display: 'Apr 01, 2024 - Mar 31, 2025' },
];

const TYPE_BADGES: Record<string, string> = {
  asset: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  liability: 'bg-rose-50 text-rose-700 border-rose-200',
  equity: 'bg-purple-50 text-purple-700 border-purple-200',
  income: 'bg-blue-50 text-blue-700 border-blue-200',
  expense: 'bg-amber-50 text-amber-700 border-amber-200',
};

export function LedgerMasterReport() {
  const [selectedFY, setSelectedFY] = useState('FY 2026-2027');
  const [startDate, setStartDate] = useState('2026-04-01');
  const [endDate, setEndDate] = useState('2027-03-31');
  const [selectedType, setSelectedType] = useState('Select Type');
  const [selectedGroup, setSelectedGroup] = useState('Select Account Group');
  const [selectedCategory, setSelectedCategory] = useState('Select Category');
  const [hideZeroEntries, setHideZeroEntries] = useState(true);
  const [showOpeningCurrent, setShowOpeningCurrent] = useState(true);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Filter params sent to query
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: '2026-04-01',
    endDate: '2027-03-31',
    type: 'Select Type',
    accountGroup: 'Select Account Group',
    category: 'Select Category',
    hideZeroEntries: true,
  });

  const { data: resData, isLoading, isError } = useQuery({
    queryKey: ['accounts', 'reports', 'ledger-master', appliedFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appliedFilters.startDate) params.append('startDate', appliedFilters.startDate);
      if (appliedFilters.endDate) params.append('endDate', appliedFilters.endDate);
      if (appliedFilters.type && appliedFilters.type !== 'Select Type') params.append('type', appliedFilters.type);
      if (appliedFilters.accountGroup && appliedFilters.accountGroup !== 'Select Account Group') params.append('accountGroup', appliedFilters.accountGroup);
      if (appliedFilters.category && appliedFilters.category !== 'Select Category') params.append('category', appliedFilters.category);
      if (appliedFilters.hideZeroEntries) params.append('hideZeroEntries', 'true');

      const { data } = await api.get(`/api/accounts/reports/ledger-master?${params.toString()}`);
      return data.data;
    }
  });

  const handleFYChange = (fyLabel: string) => {
    setSelectedFY(fyLabel);
    const fy = FINANCIAL_YEARS.find(f => f.label === fyLabel);
    if (fy) {
      setStartDate(fy.startDate);
      setEndDate(fy.endDate);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      startDate,
      endDate,
      type: selectedType,
      accountGroup: selectedGroup,
      category: selectedCategory,
      hideZeroEntries,
    });
  };

  const handleResetFilters = () => {
    setSelectedFY('FY 2026-2027');
    setStartDate('2026-04-01');
    setEndDate('2027-03-31');
    setSelectedType('Select Type');
    setSelectedGroup('Select Account Group');
    setSelectedCategory('Select Category');
    setHideZeroEntries(true);
    setShowOpeningCurrent(true);

    setAppliedFilters({
      startDate: '2026-04-01',
      endDate: '2027-03-31',
      type: 'Select Type',
      accountGroup: 'Select Account Group',
      category: 'Select Category',
      hideZeroEntries: true,
    });
  };

  const formatCurrency = (val: number | undefined | null) => {
    const num = val || 0;
    return `₹${Math.abs(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const items: LedgerMasterItem[] = resData?.items || [];
  const meta = resData?.meta || {};
  const groupsList = meta.accountGroups || [];
  const categoriesList = meta.categories || [];

  const currentFYDisplay = FINANCIAL_YEARS.find(f => f.label === selectedFY)?.display || `${startDate} - ${endDate}`;

  const handleDownloadCSV = () => {
    if (!items.length) return;
    const rows = [
      ['Code', 'Ledger Name', 'Type', 'Account Group', 'Category', 'Opening Balance', 'Debit', 'Credit', 'Current Balance'],
      ...items.map(i => [
        i.code,
        i.name,
        i.type,
        i.primaryGroup,
        i.subGroup,
        i.openingBalance,
        i.debit,
        i.credit,
        i.currentBalance,
      ]),
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `All_Ledger_Master_Report_${selectedFY.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  };

  return (
    <div className="space-y-6">
      {/* Filters Panel (Exact Layout from Reference Image) */}
      <div className="bg-white dark:bg-slate-950 p-5 rounded-xl border border-border/80 shadow-sm space-y-4">
        <h3 className="text-lg font-bold tracking-tight text-foreground">Filters</h3>

        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground font-medium mb-1">Select Financial Year</label>
              <select
                value={selectedFY}
                onChange={(e) => handleFYChange(e.target.value)}
                className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm"
              >
                {FINANCIAL_YEARS.map(fy => (
                  <option key={fy.label} value={fy.label}>{fy.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground font-medium mb-1">Transactions Date Range</label>
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={currentFYDisplay}
                  className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm w-full pr-9"
                />
                <Calendar className="w-4 h-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground font-medium mb-1">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm"
              >
                <option>Select Type</option>
                <option value="Asset">Asset</option>
                <option value="Liability">Liability</option>
                <option value="Equity">Equity</option>
                <option value="Income">Income</option>
                <option value="Expense">Expense</option>
              </select>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground font-medium mb-1">Account Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm"
              >
                <option>Select Account Group</option>
                {groupsList.map((g: any) => (
                  <option key={g._id || g.name} value={g.name}>{g.name} ({g.type})</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground font-medium mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-input rounded-md text-sm px-3 py-2 bg-background font-medium shadow-sm"
              >
                <option>Select Category</option>
                {categoriesList.map((c: string) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Checkboxes Row */}
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideZeroEntries}
                onChange={(e) => setHideZeroEntries(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
              />
              Hide Zero - Entry Accounts
            </label>

            <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOpeningCurrent}
                onChange={(e) => setShowOpeningCurrent(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
              />
              Show Opening & Current Balances
            </label>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-3">
              <button
                onClick={handleApplyFilters}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium text-sm px-6 h-9 rounded-md shadow-sm transition-colors"
              >
                Apply Filters
              </button>

              <button
                onClick={handleResetFilters}
                className="border border-input hover:bg-muted text-foreground font-medium text-sm px-6 h-9 rounded-md shadow-sm transition-colors"
              >
                Reset Filters
              </button>
            </div>

            {/* Export Menu */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="h-9 px-4 font-medium flex items-center gap-2 border-input shadow-sm"
              >
                <Download className="w-4 h-4 text-muted-foreground" />
                Export Report
              </Button>

              {showDownloadMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-50 py-1 text-sm">
                  <button
                    onClick={handleDownloadCSV}
                    className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-foreground font-medium"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                    CSV Spreadsheet
                  </button>
                  <button
                    onClick={() => { window.print(); setShowDownloadMenu(false); }}
                    className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-foreground font-medium"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    PDF / Print View
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      {isLoading ? (
        <Skeleton className="h-[400px] w-full rounded-xl" />
      ) : isError ? (
        <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 text-sm font-medium">
          Failed to load All Ledger Master Report.
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden shadow-sm bg-white dark:bg-slate-950">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-border text-muted-foreground font-semibold text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 w-16">Code</th>
                  <th className="px-5 py-3">Ledger Name</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Account Group</th>
                  <th className="px-5 py-3">Category</th>
                  {showOpeningCurrent && <th className="px-5 py-3 text-right">Opening Balance</th>}
                  <th className="px-5 py-3 text-right">Debit (₹)</th>
                  <th className="px-5 py-3 text-right">Credit (₹)</th>
                  {showOpeningCurrent && <th className="px-5 py-3 text-right">Current Balance</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={showOpeningCurrent ? 9 : 7} className="px-5 py-12 text-center text-muted-foreground">
                      No ledger accounts match the selected filters.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item._id} className="hover:bg-slate-50/70 dark:hover:bg-slate-900/50 transition-colors">
                      <td className="px-5 py-3 font-mono text-xs font-semibold text-muted-foreground">{item.code}</td>
                      <td className="px-5 py-3 font-medium text-foreground">{item.name}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border uppercase tracking-wider ${TYPE_BADGES[item.type?.toLowerCase()] || 'bg-slate-100 text-slate-700'}`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground text-xs">{item.primaryGroup}</td>
                      <td className="px-5 py-3 text-muted-foreground text-xs">{item.subGroup}</td>
                      {showOpeningCurrent && (
                        <td className="px-5 py-3 text-right font-mono text-xs text-muted-foreground">
                          {formatCurrency(item.openingBalance)}
                        </td>
                      )}
                      <td className="px-5 py-3 text-right font-mono text-xs font-medium text-foreground">
                        {formatCurrency(item.debit)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-xs font-medium text-foreground">
                        {formatCurrency(item.credit)}
                      </td>
                      {showOpeningCurrent && (
                        <td className="px-5 py-3 text-right font-mono text-xs font-bold text-emerald-700 dark:text-emerald-400">
                          {formatCurrency(item.currentBalance)}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot className="bg-slate-100/80 dark:bg-slate-900 border-t-2 border-border font-bold text-xs">
                  <tr>
                    <td colSpan={5} className="px-5 py-3 text-foreground uppercase tracking-wider">Total ({items.length} accounts)</td>
                    {showOpeningCurrent && (
                      <td className="px-5 py-3 text-right font-mono text-muted-foreground">{formatCurrency(meta.totalOpening)}</td>
                    )}
                    <td className="px-5 py-3 text-right font-mono text-foreground">{formatCurrency(meta.totalDebit)}</td>
                    <td className="px-5 py-3 text-right font-mono text-foreground">{formatCurrency(meta.totalCredit)}</td>
                    {showOpeningCurrent && (
                      <td className="px-5 py-3 text-right font-mono text-emerald-700 dark:text-emerald-400">{formatCurrency(meta.totalCurrent)}</td>
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
